import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
  type GridRowModel,
} from '@mui/x-data-grid';

import PreviewDialog from './PreviewDialog';
import { useConfigSectionEditor } from './useConfigSectionEditor';
import { usePendingGridEdits } from './usePendingGridEdits';
import {
  SaveSuccessSnackbar,
  SectionAlerts,
  SectionProvenance,
  SectionTitle,
  UnsavedChangesGuard,
} from './SectionEditorChrome';

interface AliasRow {
  id: number;
  alternativeName: string;
  canonicalName: string;
}

function recordToRows(aliases: Record<string, string>): AliasRow[] {
  return Object.entries(aliases).map(([alt, canonical], i) => ({
    id: i,
    alternativeName: alt,
    canonicalName: canonical,
  }));
}

/**
 * Keys keep the user's casing (matching is case-insensitive server-side), so a
 * loaded map round-trips unchanged and the page is not "dirty" on load.
 */
function rowsToRecord(rows: AliasRow[]): Record<string, string> {
  const result: Record<string, string> = {};
  for (const row of rows) {
    const key = row.alternativeName.trim();
    if (key) {
      result[key] = row.canonicalName.trim();
    }
  }
  return result;
}

/**
 * Catch mistakes that would otherwise be silently dropped or produce ambiguous
 * resolution: duplicate alternative names, blank canonical names, and chains/cycles
 * (a canonical name that is itself another row's alternative name).
 */
function validateAliasRows(rows: AliasRow[]): string | null {
  const byAlt = new Map<string, AliasRow>();
  for (const row of rows) {
    const alt = row.alternativeName.trim().toLowerCase();
    if (!alt) continue;
    if (byAlt.has(alt)) {
      return `"${row.alternativeName.trim()}" is listed more than once. Each alternative name can map to only one canonical name.`;
    }
    if (!row.canonicalName.trim()) {
      return `"${row.alternativeName.trim()}" needs a canonical name.`;
    }
    byAlt.set(alt, row);
  }
  for (const [alt, row] of byAlt) {
    const target = row.canonicalName.trim().toLowerCase();
    const next = byAlt.get(target);
    if (next && target !== alt) {
      return (
        `"${row.alternativeName.trim()}" → "${row.canonicalName.trim()}" points at another alternative ` +
        `name ("${next.alternativeName.trim()}" → "${next.canonicalName.trim()}"). Aliases resolve in one ` +
        'hop, so the canonical name must be a final client code. Map both to the same canonical name.'
      );
    }
  }
  return null;
}

/**
 * Client Aliases settings section.
 *
 * Editable DataGrid mapping alternative client names (map keys) to canonical Automate
 * client codes. Supports inline cell editing, row add/delete, and preview-before-apply
 * via PreviewDialog; state and save flow via useConfigSectionEditor.
 */
export default function ClientAliasesSection() {
  const editor = useConfigSectionEditor<AliasRow, Record<string, string>>({
    section: 'client-aliases',
    label: 'Client Aliases',
    toRows: recordToRows,
    fromRows: rowsToRecord,
    validateRows: validateAliasRows,
  });
  const { rows, setRows } = editor;
  const pendingEdits = usePendingGridEdits<AliasRow>();

  function handleAddRow() {
    const newId = rows.length > 0 ? Math.max(...rows.map((r) => r.id)) + 1 : 0;
    setRows((prev) => [...prev, { id: newId, alternativeName: '', canonicalName: '' }]);
  }

  function handleDeleteRow(id: number) {
    setRows((prev) => prev.filter((r) => r.id !== id));
  }

  function handleProcessRowUpdate(newRow: GridRowModel): AliasRow {
    const updated = newRow as AliasRow;
    setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    return updated;
  }

  const columns: GridColDef[] = [
    {
      field: 'canonicalName',
      headerName: 'Canonical Name',
      flex: 1,
      editable: true,
    },
    {
      field: 'alternativeName',
      headerName: 'Alternative Name',
      flex: 1,
      editable: true,
    },
    {
      field: '_actions',
      headerName: '',
      width: 56,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams<AliasRow>) => (
        <IconButton
          size="small"
          onClick={() => handleDeleteRow(params.row.id)}
          aria-label="Delete row"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ),
    },
  ];

  if (editor.isLoading && !editor.data) {
    return (
      <Box sx={{ py: 2 }}>
        <Skeleton variant="rounded" height={200} sx={{ mb: 2 }} />
        <Skeleton variant="text" width={140} />
      </Box>
    );
  }

  return (
    <Box>
      <SectionTitle title="Client Aliases" isDirty={editor.isDirty} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Maps alternative client name variants to their canonical Automate client codes. Each
        platform may use a different name for the same organization (e.g., ThreatLocker uses full
        legal names like &ldquo;Ambest, Inc&rdquo; while Automate uses &ldquo;AB&rdquo;). Aliases
        unify these so devices are grouped under one client during cross-referencing. Matching is
        case-insensitive and resolves in a single hop.
      </Typography>

      <SectionAlerts
        loadError={editor.loadError}
        hasConflict={editor.hasConflict}
        validationError={editor.validationError}
        onDiscard={editor.discardChanges}
      />

      <DataGrid
        rows={rows}
        columns={columns}
        processRowUpdate={handleProcessRowUpdate}
        {...pendingEdits.gridProps}
        disableRowSelectionOnClick
        hideFooter={rows.length <= 100}
        autoHeight
        sx={{ mb: 1.5 }}
      />

      <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
        <Button size="small" startIcon={<AddIcon />} onClick={handleAddRow} variant="outlined">
          Add Alias
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => editor.openPreview(pendingEdits.withPendingEdits(rows))}
          disabled={
            editor.preview.isPending ||
            (!editor.isDirty && !pendingEdits.editing) ||
            !!editor.validationError
          }
        >
          {editor.preview.isPending ? 'Computing...' : 'Preview Changes'}
        </Button>
        {editor.isDirty && (
          <Button size="small" color="inherit" onClick={editor.discardChanges}>
            Discard
          </Button>
        )}
      </Box>

      <SectionProvenance record={editor.record} />

      <PreviewDialog
        open={editor.preview.open}
        preview={editor.preview.data}
        previewLoading={editor.preview.isPending}
        error={editor.preview.error}
        applying={editor.applying}
        applyError={editor.applyError}
        writeDisabledReason={editor.writeDisabledReason}
        onApply={() => void editor.apply()}
        onCancel={editor.closePreview}
      />
      <SaveSuccessSnackbar message={editor.successMessage} onClose={editor.clearSuccess} />
      <UnsavedChangesGuard when={editor.isDirty} />
    </Box>
  );
}
